import { useCallback, useMemo, useReducer } from "react";

import {
    COMPANY_LEVELS,
    COMPANY_TYPES,
    ENDPOINT_PATH_SEGMENTS,
    ENDPOINTS,
    Filter,
    FILTER_NAMES,
    FilterDropdownOptions,
    FilteredValue,
    getCustomResourceLimits,
    NOT_FILTER_NAMES,
    QueryParams,
    ResourceLimitSettings,
    TABLE_NAMES,
    updateParamsWithCorrectSpecIdValue,
    useApi
} from "shared";

import { INITIAL_RELOAD_ITEMS } from "./constants";
import {
    distributeFilteredValues,
    setQueryParamsHelperFunctions
} from "./functions";
import { useDebouncedReload } from "./hooks";
import {
    CHANGE_CUSTOM_RESOURCE_LIMITS,
    CHANGE_ROOT_COMPANY,
    CHANGE_ROOT_COMPANY_LEVEL,
    RELOAD_ITEMS,
    REMOVE_RECURRING_RELOAD,
    RESET_ALL_PARAMS,
    RESET_FILTER_PARAMS,
    SET_COMPANY_FILTERED_VALUES,
    SET_FILTERED_VALUES,
    SET_QUERY_PARAMS,
    SET_RESOURCE,
    TOGGLE_TABLE_ROW_DESELECTION,
    TRIGGER_ROOT_COMPANY,
    UPDATE_FILTER_PARAMS
} from "./paramsActions";
import ParamsContext from "./paramsContext";
import ParamsReducer from "./paramsReducer";
import { ParamsStateProps, SetQueryParamsOptions } from "./types";

const ParamsState = ({ children }: ParamsStateProps) => {
    const initialState = {
        resource: null,
        filteredItemsCount: 0,
        queryParams: {},
        filterDropdownOptions: {},
        filterQueryParams: {},
        filteredValues: [],
        companyFilteredValues: [],
        stringParams: "",
        tableRowDeselectionToggle: false,
        rootCompany: null,
        rootCompanyLevel: COMPANY_LEVELS.Basic,
        rootCompanyType: COMPANY_TYPES.Client,
        rootClientCompanyId: null,
        rootCustomResourceLimitSettings: null,
        canInviteUsers: false,
        reloadRootCompany: 0,
        isRecurringReload: false,
        reloadItems: INITIAL_RELOAD_ITEMS
    };

    const [state, dispatch] = useReducer(ParamsReducer, initialState);
    const { getData } = useApi();

    const setResource = (resource: TABLE_NAMES) =>
        dispatch({ type: SET_RESOURCE, payload: resource });

    const setQueryParams = (parameters: SetQueryParamsOptions) => {
        const {
            params,
            resetPage,
            filteredValues,
            resetParams,
            removeFilterOptions,
            removeSelected,
            rootInfo
        } = parameters;

        const { getSearchParams, getParamsApprovable } =
            setQueryParamsHelperFunctions(params);

        const paramsWithCorrectSpecId =
            updateParamsWithCorrectSpecIdValue(params);

        const updatedParams = {
            ...paramsWithCorrectSpecId,
            ...getSearchParams(),
            ...getParamsApprovable(),
            ...(Boolean(resetPage) && {
                [NOT_FILTER_NAMES.Page]: "",
                [NOT_FILTER_NAMES.Selected]: ""
            }),
            ...(Boolean(removeSelected) && {
                [NOT_FILTER_NAMES.Selected]: ""
            })
        };

        dispatch({
            type: SET_QUERY_PARAMS,
            payload: {
                rootCompany: rootInfo?.name ?? null,
                rootCompanyLevel: rootInfo?.level ?? null,
                rootCompanyType: rootInfo?.type ?? null,
                rootClientCompanyId: rootInfo?.rootClientCompanyId,
                rootCustomResourceLimitSettings:
                    rootInfo?.customResourceLimitSettings,
                canInviteUsers: rootInfo?.canInviteUsers ?? null,
                resetParams: Boolean(resetParams),
                removeFilterOptions: Boolean(removeFilterOptions),
                updatedParams,
                ...distributeFilteredValues(filteredValues)
            }
        });
    };

    const changeRootCompany = useCallback(
        async (rootId: string, additionalParams?: QueryParams) => {
            const updatedParams = {
                [FILTER_NAMES.RootCompany]: rootId,
                ...(additionalParams || {})
            };

            try {
                const rootCompanyAttributes = await getData(
                    `${ENDPOINTS.Companies}/${rootId}${ENDPOINT_PATH_SEGMENTS.Attributes}`
                );

                const customResourceLimits = await getCustomResourceLimits(
                    rootId,
                    getData
                );

                dispatch({
                    type: CHANGE_ROOT_COMPANY,
                    payload: {
                        params: updatedParams,
                        rootCompanyLevel:
                            rootCompanyAttributes.data.level ?? null,
                        rootCompanyType:
                            rootCompanyAttributes.data.type ?? null,
                        rootClientCompanyId:
                            rootCompanyAttributes.data.root_client_company_id,
                        rootCustomResourceLimitSettings: customResourceLimits,
                        canInviteUsers:
                            rootCompanyAttributes.data.user_invites_enabled
                    }
                });
            } catch {
                dispatch({
                    type: CHANGE_ROOT_COMPANY,
                    payload: {
                        params: updatedParams,
                        rootCompanyLevel: null,
                        rootCompanyType: null,
                        rootClientCompanyId: undefined,
                        rootCustomResourceLimitSettings: null,
                        canInviteUsers: false
                    }
                });
            }
        },
        [getData]
    );

    const changeRootCompanyLevel = useCallback(
        (level: COMPANY_LEVELS) =>
            dispatch({ type: CHANGE_ROOT_COMPANY_LEVEL, payload: level }),
        []
    );

    const changeCustomResourceLimits = (
        settings: ResourceLimitSettings | null
    ) => dispatch({ type: CHANGE_CUSTOM_RESOURCE_LIMITS, payload: settings });

    const triggerRootCompany = (hasTableReload: boolean) =>
        dispatch({ type: TRIGGER_ROOT_COMPANY, payload: hasTableReload });

    const setFilteredValues = (
        data: FilteredValue[],
        filterDropdownOptions?: FilterDropdownOptions
    ) =>
        dispatch({
            type: SET_FILTERED_VALUES,
            payload: {
                data,
                filterDropdownOptions
            }
        });

    const setCompanyFilteredValues = (
        filteredValues: Filter[],
        filterDropdownOptions: FilterDropdownOptions
    ) => {
        dispatch({
            type: SET_COMPANY_FILTERED_VALUES,
            payload: {
                filteredValues,
                filterDropdownOptions
            }
        });
    };

    const updateFilterParams = (
        updatedParams: QueryParams,
        filteredValues: FilteredValue
    ) =>
        dispatch({
            type: UPDATE_FILTER_PARAMS,
            payload: {
                updatedParams,
                ...distributeFilteredValues(filteredValues)
            }
        });

    const resetFilterParams = (onlyCompanies?: boolean) =>
        dispatch({
            type: RESET_FILTER_PARAMS,
            payload: Boolean(onlyCompanies)
        });

    const resetAllParams = () => dispatch({ type: RESET_ALL_PARAMS });

    const toggleTableRowDeselection = () =>
        dispatch({ type: TOGGLE_TABLE_ROW_DESELECTION });

    const removeRecurringReload = () =>
        dispatch({ type: REMOVE_RECURRING_RELOAD });

    const setReloadItems = (
        resource: TABLE_NAMES | TABLE_NAMES[],
        isRecurring?: boolean
    ) =>
        dispatch({
            type: RELOAD_ITEMS,
            payload: { resource, isRecurring: Boolean(isRecurring) }
        });

    const debouncedReload = useDebouncedReload<TABLE_NAMES | TABLE_NAMES[]>(
        setReloadItems
    );

    const debouncedTrigger = useDebouncedReload<boolean>(triggerRootCompany);

    const setDebouncedReloadItems = useCallback(
        (resource: TABLE_NAMES) => debouncedReload(resource),
        [debouncedReload]
    );

    const setDebouncedTriggerRootCompany = useCallback(
        () => debouncedTrigger(true),
        [debouncedTrigger]
    );

    const value = useMemo(
        () => ({
            resource: state.resource,
            filteredItemsCount: state.filteredItemsCount,
            queryParams: state.queryParams,
            filterDropdownOptions: state.filterDropdownOptions,
            filterQueryParams: state.filterQueryParams,
            filteredValues: state.filteredValues,
            companyFilteredValues: state.companyFilteredValues,
            stringParams: state.stringParams,
            tableRowDeselectionToggle: state.tableRowDeselectionToggle,
            rootCompany: state.rootCompany,
            rootCompanyLevel: state.rootCompanyLevel,
            rootCompanyType: state.rootCompanyType,
            rootClientCompanyId: state.rootClientCompanyId,
            rootCustomResourceLimitSettings:
                state.rootCustomResourceLimitSettings,
            canInviteUsers: state.canInviteUsers,
            reloadRootCompany: state.reloadRootCompany,
            isRecurringReload: state.isRecurringReload,
            reloadItems: state.reloadItems,
            setResource,
            resetFilterParams,
            resetAllParams,
            toggleTableRowDeselection,
            setQueryParams,
            changeRootCompany,
            changeRootCompanyLevel,
            changeCustomResourceLimits,
            triggerRootCompany,
            setFilteredValues,
            setCompanyFilteredValues,
            updateFilterParams,
            removeRecurringReload,
            setReloadItems,
            setDebouncedReloadItems,
            setDebouncedTriggerRootCompany
        }),
        [
            state.resource,
            state.filteredItemsCount,
            state.queryParams,
            state.filterDropdownOptions,
            state.filterQueryParams,
            state.filteredValues,
            state.companyFilteredValues,
            state.stringParams,
            state.tableRowDeselectionToggle,
            state.rootCompany,
            state.rootCompanyLevel,
            state.rootCompanyType,
            state.rootClientCompanyId,
            state.rootCustomResourceLimitSettings,
            state.canInviteUsers,
            state.reloadRootCompany,
            state.isRecurringReload,
            state.reloadItems,
            changeRootCompany,
            changeRootCompanyLevel,
            setDebouncedReloadItems,
            setDebouncedTriggerRootCompany
        ]
    );

    return <ParamsContext value={value}>{children}</ParamsContext>;
};

export default ParamsState;
